// The course footer, the same in both courses: the product mark, the course motto, how the forms are checked, two
// columns of links and the Suvaani line. Prakrit shows its motto as tappable Prakrit; Sanskrit as Sanskrit text.

import type { FC, ReactNode } from 'react'
import type { Course } from '@/lib/plans'
import type { FooterLink } from '@/constants/site'

import Link from 'next/link'
import { ShieldCheck } from 'lucide-react'
import { COURSE_SOCIAL_LINKS, FOOTER_LINKS, LEGAL_LINKS } from '@/constants/site'
import { TAGLINE } from '@/prakrit/lib/content/ui-strings'
import { SuvaaniMark } from '@/components/app/site-header'
import { SocialLinks } from '@/components/app/social-links'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'

const MOTTO: Record<Course, { text: ReactNode, en: string, check: string }> = {
    prakrit: {
        text: <PrakritText text={TAGLINE.pra} />,
        en: TAGLINE.en,
        check: 'Every form is built from the rules of the course book and checked against its examples.'
    },
    sanskrit: {
        text: <span lang='sa'>संस्कृतं सरलतया शिक्षामहे।</span>,
        en: 'We learn Sanskrit with ease.',
        check: 'Every form is cross-checked against standard reference grammars; anything awaiting a scholar’s review is marked.'
    }
}

const Column: FC<{ title: string, links: FooterLink[] }> = ({ title, links }): ReactNode => (
    <div className='flex flex-col gap-3'>
        <h2 className='font-sans text-base font-bold text-foreground'>{title}</h2>
        <ul className='flex flex-col gap-2.5 text-base font-semibold'>
            {links.map(l => (
                <li key={l.href}>
                    <Link href={l.href} className='text-foreground/75 transition-colors hover:text-foreground'>{l.en}</Link>
                </li>
            ))}
        </ul>
    </div>
)

export const SiteFooter: FC<{ course?: Course }> = ({ course = 'prakrit' }): ReactNode => {
    const motto = MOTTO[course]
    const links = FOOTER_LINKS[course]
    const other: Course = course === 'prakrit' ? 'sanskrit' : 'prakrit'

    return (
        <footer className='mt-20 border-t border-border bg-surface-muted/60'>
            <div className='mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr]'>
                <div className='flex flex-col gap-4'>
                    <SuvaaniMark course={course} />
                    <div className='flex flex-col gap-1'>
                        <p className='pra pra-md flex flex-wrap items-center gap-2'>{motto.text}</p>
                        <p className='text-base font-semibold text-foreground/75'>{motto.en}</p>
                    </div>
                    <p className='flex max-w-md items-start gap-2 text-[0.9375rem] font-medium text-foreground/70'>
                        <ShieldCheck className='mt-0.5 size-4 shrink-0 text-success' aria-hidden='true' />
                        {motto.check}
                    </p>
                    <div className='flex flex-col gap-2'>
                        <p className='text-sm font-bold text-foreground'>Follow {course === 'prakrit' ? 'Prakrit Suvaani' : 'Sanskrit Suvaani'}</p>
                        <SocialLinks brand={course === 'prakrit' ? 'Prakrit Suvaani' : 'Sanskrit Suvaani'} links={COURSE_SOCIAL_LINKS[course]} />
                    </div>
                </div>
                <Column title='Learn' links={links.learn} />
                <Column title='Grammar Lab' links={links.grammar} />
            </div>
            <div className='border-t border-border'>
                <p className='mx-auto flex max-w-7xl flex-wrap items-center gap-x-5 gap-y-1.5 px-4 py-5 text-sm font-semibold text-foreground/70 sm:px-6'>
                    <span>© Suvaani</span>
                    <Link href='/' className='hover:text-foreground'>Suvaani home</Link>
                    <Link href={other === 'prakrit' ? '/prakrit' : '/sanskrit'} className='hover:text-foreground'>{other === 'prakrit' ? 'Prakrit Suvaani' : 'Sanskrit Suvaani'}</Link>
                    <Link href='/pricing' className='hover:text-foreground'>Pricing and Premium</Link>
                    {LEGAL_LINKS.map(link => (
                        <Link key={link.href} href={link.href} className='hover:text-foreground'>{link.en}</Link>
                    ))}
                </p>
            </div>
        </footer>
    )
}
